import React, { useState, useEffect, useCallback, useRef } from "react";
import { Card, Col, Row, Statistic, Tabs, Form, Input, Button, Select, Alert, Space, Typography } from "antd";
import { UserOutlined, TransactionOutlined, DollarOutlined, SettingOutlined } from "@ant-design/icons";
import { getTransactions } from "../services/transactionService";
import TransactionList from "../components/transactions/TransactionList";
import FeeManagementForm from "../components/fees/FeeManagementForm";
import { message } from "antd";
import useGetTransactions from "../hooks/transactions/useGetTransactions";
import { useTransactionSummary, useUserSummary } from "../hooks/useAnalytics";
import useGetManualPayouts from "../hooks/useGetManualPayouts";
import ManualPayoutList from "../components/manual-payouts/ManualPayoutList";
import ManualPayoutDetailModal from "../components/manual-payouts/ManualPayoutDetailModal";
import { getTransfer } from "../services/transferService";

const Dashboard = () => {
  const [loading, setLoading] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [filters, setFilters] = useState({});
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 1200);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Manual payout state
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [payoutModalVisible, setPayoutModalVisible] = useState(false);
  const [transferDetails, setTransferDetails] = useState(null);
  const [transferDetailsLoading, setTransferDetailsLoading] = useState(false);

  const observerTarget = useRef(null);
  const filtersRef = useRef({});
  const firstMount = useRef(true);
  const isResetting = useRef(false);
  const LIMIT = 15;

  // Hooks for different tabs
  const pendingApprovalQuery = useGetTransactions(
    { status: 'pending-approval' },
    { enabled: activeTab === 'pending-approval' }
  );
  
  const approvedQuery = useGetTransactions(
    { status: 'approved' },
    { enabled: activeTab === 'approved' }
  );
  
  const pendingCashCollectionQuery = useGetTransactions(
    { status: 'pending-disburse' },
    { enabled: activeTab === 'pending-cash-collection' }
  );

  // Analytics hooks
  const transactionSummaryQuery = useTransactionSummary();
  const userSummaryQuery = useUserSummary();
  
  // Manual payouts hook
  const manualPayoutsQuery = useGetManualPayouts();

  // Update the ref whenever filters change
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  // Fetch transactions with pagination
  const fetchTransactions = useCallback(
    async (reset = false) => {
      // Prevent multiple simultaneous calls
      if (loading) {
        return;
      }

      // Skip if we're not resetting and there are no more items
      if (!reset && !hasMore) {
        return;
      }

      try {
        setLoading(true);

        // Build query parameters object
        const queryParams = {
          limit: LIMIT,
        };

        // Set pagination cursor if needed
        if (!reset && nextCursor) {
          queryParams.next_cursor = nextCursor;
        }

        // Add filters if they exist - use the ref instead of the state
        const currentFilters = filtersRef.current;
        if (
          currentFilters.dateRange &&
          Array.isArray(currentFilters.dateRange) &&
          currentFilters.dateRange.length === 2
        ) {
          try {
            const startDate = new Date(currentFilters.dateRange[0]); // Handle Moment.js objects if needed
            const endDate = new Date(currentFilters.dateRange[1]);

            // Ensure we have valid Date objects
            if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
              // Start date should always be the beginning of the day (00:00:00)
              const startOfDay = new Date(startDate);
              startOfDay.setHours(0, 0, 0, 0);
              queryParams.from = startOfDay.valueOf();

              // For end date logic
              const now = new Date();

              // Check if the end date is today
              const isToday =
                endDate.getFullYear() === now.getFullYear() &&
                endDate.getMonth() === now.getMonth() &&
                endDate.getDate() === now.getDate();

              if (isToday) {
                // If end date is today, use current time
                queryParams.to = now.valueOf();
              } else {
                // If end date is in the past, use end of that day (23:59:59.999)
                const endOfDay = new Date(endDate);
                endOfDay.setHours(23, 59, 59, 999);
                queryParams.to = endOfDay.valueOf();
              }

              console.log("Date filter applied:", {
                from: new Date(queryParams.from).toLocaleString(),
                to: new Date(queryParams.to).toLocaleString(),
              });
            } else {
              console.error("Invalid date values in dateRange filter");
            }
          } catch (error) {
            console.error("Error processing date range:", error);
          }
        }
        if (currentFilters.type) {
          // Handle type as comma-separated list if it's an array
          if (Array.isArray(currentFilters.type)) {
            queryParams.type = currentFilters.type.join(",");
          } else {
            queryParams.type = currentFilters.type;
          }
        }
        if (currentFilters.status) {
          queryParams.status = currentFilters.status.join(",");
        }
        if (currentFilters.disburse_type) {
          if (Array.isArray(currentFilters.disburse_type)) {
            queryParams.disburse_type = currentFilters.disburse_type.join(",");
          } else {
            queryParams.disburse_type = currentFilters.disburse_type;
          }
        }

        // Add search params if they exist
        if (currentFilters.reference && currentFilters.reference.trim()) {
          queryParams.reference = currentFilters.reference.trim();
        }

        if (currentFilters.user_id && currentFilters.user_id.trim()) {
          queryParams.user_id = currentFilters.user_id.trim();
        }

        if (currentFilters.profile_id && currentFilters.profile_id.trim()) {
          queryParams.profile_id = currentFilters.profile_id.trim();
        }

        if (currentFilters.user_name && currentFilters.user_name.trim()) {
          queryParams.user_name = currentFilters.user_name.trim();
        }

        // If any search parameter is present, don't use cursor-based pagination
        const isSearching = !!(
          queryParams.reference ||
          queryParams.user_id ||
          queryParams.profile_id ||
          queryParams.user_name
        );

        // Remove next_cursor if we're searching by specific IDs
        if (isSearching && queryParams.next_cursor) {
          delete queryParams.next_cursor;
          console.log("Search mode: Disabling cursor-based pagination");
        }

        const response = await getTransactions(queryParams);

        // Log the entire response to see its structure
        console.log("API Response:", response);

        // Extract transactions array and total from the response
        const newTransactions = response.transactions || [];

        // For cursor-based pagination, we need to accumulate the total
        // This will be the length of all transactions we've seen so far
        setTransactions((prev) => {
          const updatedTransactions = reset
            ? newTransactions
            : [...prev, ...newTransactions];

          // Analytics are now handled by the analytics API hooks

          return updatedTransactions;
        });

        // Update pagination state
        setNextCursor(response.next_cursor);
        setHasMore(!!response.next_cursor);
      } catch (error) {
        console.error("Error fetching transactions:", error);
        message.error("Failed to fetch transactions. Please try again.");
        setHasMore(false);
      } finally {
        setLoading(false);
        // Reset the isResetting flag after fetch is complete
        if (isResetting.current) {
          isResetting.current = false;
        }
      }
    },
    [loading, hasMore, nextCursor]
  ); // Add required dependencies without causing infinite loops

  // Handle initial load and filter changes
  useEffect(() => {
    // Skip effect if component is still loading data
    if (loading) return;

    // Skip if we're currently in a reset operation
    if (isResetting.current) return;

    if (firstMount.current) {
      // This is the first render, so we'll make the call and mark it done
      firstMount.current = false;
      fetchTransactions(true);
    } else if (Object.keys(filters).length > 0) {
      // Only make additional calls when filters actually change with values
      // Reset pagination state before fetching with new filters
      setNextCursor(null);
      setHasMore(true);
      fetchTransactions(true);
    }
  }, [filters]); // Only depend on filters, not fetchTransactions

  // Set up intersection observer for infinite scrolling
  useEffect(() => {
    if (!observerTarget.current || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting && hasMore && !loading) {
          fetchTransactions(false);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(observerTarget.current);

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
      observer.disconnect();
    };
  }, [hasMore, loading, fetchTransactions]);

  // Check window size on resize
  useEffect(() => {
    const checkWindowSize = () => {
      setIsSmallScreen(window.innerWidth < 1200);
    };

    window.addEventListener("resize", checkWindowSize);
    return () => window.removeEventListener("resize", checkWindowSize);
  }, []);

  // Handle refresh - now resets filters as well
  const handleRefresh = useCallback(() => {
    // Set the resetting flag to prevent filter effect from triggering
    isResetting.current = true;

    // Reset all state values
    setTransactions([]); // Clear existing transactions
    setNextCursor(null); // Reset pagination cursor
    setHasMore(true); // Reset hasMore flag

    // Clear filters ref directly
    filtersRef.current = {};

    // Scroll to top of the page to avoid the intersection observer triggering immediately
    window.scrollTo(0, 0);

    // Fetch transactions immediately instead of using a timeout
    fetchTransactions(true);

    // Clear filters state AFTER starting the fetch to prevent the filters effect from triggering a second fetch
    // This change should maintain all existing functionality while preventing double API calls
    setFilters({});
  }, [fetchTransactions]);

  // Manual payout handlers
  const handleViewPayoutDetails = async (transfer) => {
    try {
      setTransferDetailsLoading(true);
      setSelectedTransfer(transfer);
      const details = await getTransfer(transfer.id, true);
      setTransferDetails(details);
      setPayoutModalVisible(true);
    } catch (error) {
      console.error('Error fetching transfer details:', error);
      message.error('Failed to fetch transfer details');
    } finally {
      setTransferDetailsLoading(false);
    }
  };

  const handlePayoutSuccess = () => {
    manualPayoutsQuery.refetch();
    setPayoutModalVisible(false);
    setSelectedTransfer(null);
    setTransferDetails(null);
  };

  const handlePayoutModalClose = () => {
    setPayoutModalVisible(false);
    setSelectedTransfer(null);
    setTransferDetails(null);
  };

  // Handle filter changes
  const handleFilter = useCallback((newFilters) => {
    // Check if filters are being cleared (empty object)
    const isClearing = Object.keys(newFilters).length === 0;

    if (isClearing) {
      // If all filters are being cleared, handle like a refresh
      // But don't reset the filters since that's already happening
      isResetting.current = true;
      setTransactions([]);
      setNextCursor(null);
      setHasMore(true);
      window.scrollTo(0, 0);
      // Also trigger a fetch for all transactions
      fetchTransactions(true);
      setFilters({});
      return;
    }

    // Update filters state which will trigger the filters useEffect
    setFilters(newFilters);
  }, [fetchTransactions]);


  // Tab items configuration
  const items = [
    {
      key: 'overview',
      label: 'Overview',
      children: (
        <>
          <Row gutter={[24, 24]} style={{ marginBottom: "24px" }}>
            <Col span={12} md={6}>
              <Card>
                <Statistic
                  title="Total Users"
                  value={userSummaryQuery.data?.total_users || 0}
                  prefix={<UserOutlined style={{ color: "#1890ff" }} />}
                  valueStyle={{ color: "#1890ff" }}
                  loading={userSummaryQuery.isLoading}
                />
              </Card>
            </Col>
            <Col span={12} md={6}>
              <Card>
                <Statistic
                  title="Completed Transactions"
                  value={transactionSummaryQuery.data?.total_completed_count || 0}
                  prefix={<TransactionOutlined style={{ color: "#52c41a" }} />}
                  valueStyle={{ color: "#52c41a" }}
                  loading={transactionSummaryQuery.isLoading}
                />
              </Card>
            </Col>
            <Col span={12} md={6}>
              <Card>
                <Statistic
                  title="Pending Upgrades"
                  value={userSummaryQuery.data?.total_pending_upgrade || 0}
                  prefix={<UserOutlined style={{ color: "#faad14" }} />}
                  valueStyle={{ color: "#faad14" }}
                  loading={userSummaryQuery.isLoading}
                />
              </Card>
            </Col>
            <Col span={12} md={6}>
              <Card>
                <Statistic
                  title="Pending Review"
                  value={userSummaryQuery.data?.total_pending_review || 0}
                  prefix={<UserOutlined style={{ color: "#ff4d4f" }} />}
                  valueStyle={{ color: "#ff4d4f" }}
                  loading={userSummaryQuery.isLoading}
                />
              </Card>
            </Col>
          </Row>

          <Row style={{ marginTop: "16px" }}>
            <Col span={24}>
              <TransactionList
                transactions={transactions}
                loading={loading}
                hasMore={hasMore}
                onLoadMore={fetchTransactions}
                onRefresh={handleRefresh}
                onFilter={handleFilter}
                filters={filters}
                isSmallScreen={isSmallScreen}
                observerTarget={observerTarget}
              />
            </Col>
          </Row>
        </>
      ),
    },
    {
      key: 'pending-approval',
      label: 'Pending Approval',
      children: (
        <TransactionList
          transactions={pendingApprovalQuery.data || []}
          loading={pendingApprovalQuery.isFetching || false}
          hasMore={pendingApprovalQuery.hasNextPage || false}
          onLoadMore={pendingApprovalQuery.fetchNextPage || (() => Promise.resolve({}))}
          onRefresh={() => {}} // No refresh needed for filtered view
          onFilter={() => {}} // No filters needed for filtered view
          filters={{}}
          isSmallScreen={isSmallScreen}
          observerTarget={observerTarget}
          hideFilterAndRefresh={true} // Hide filter and refresh buttons
        />
      ),
    },
    {
      key: 'approved',
      label: 'Approved',
      children: (
        <TransactionList
          transactions={approvedQuery.data || []}
          loading={approvedQuery.isFetching || false}
          hasMore={approvedQuery.hasNextPage || false}
          onLoadMore={approvedQuery.fetchNextPage || (() => Promise.resolve({}))}
          onRefresh={() => {}} // No refresh needed for filtered view
          onFilter={() => {}} // No filters needed for filtered view
          filters={{}}
          isSmallScreen={isSmallScreen}
          observerTarget={observerTarget}
          hideFilterAndRefresh={true} // Hide filter and refresh buttons
        />
      ),
    },
    {
      key: 'pending-cash-collection',
      label: 'Pending Cash Collection',
      children: (
        <TransactionList
          transactions={pendingCashCollectionQuery.data || []}
          loading={pendingCashCollectionQuery.isFetching || false}
          hasMore={pendingCashCollectionQuery.hasNextPage || false}
          onLoadMore={pendingCashCollectionQuery.fetchNextPage || (() => Promise.resolve({}))}
          onRefresh={() => {}} // No refresh needed for filtered view
          onFilter={() => {}} // No filters needed for filtered view
          filters={{}}
          isSmallScreen={isSmallScreen}
          observerTarget={observerTarget}
          hideFilterAndRefresh={true} // Hide filter and refresh buttons
        />
      ),
    },
    {
      key: 'manual-payout',
      label: 'Manual Payout',
      children: (
        <ManualPayoutList
          transfers={manualPayoutsQuery.data || []}
          loading={manualPayoutsQuery.isFetching || false}
          hasMore={manualPayoutsQuery.hasNextPage || false}
          onLoadMore={manualPayoutsQuery.fetchNextPage || (() => Promise.resolve({}))}
          onRefresh={() => manualPayoutsQuery.refetch()}
          onViewDetails={handleViewPayoutDetails}
          isSmallScreen={isSmallScreen}
        />
      ),
    },
    {
      key: 'fee-management',
      label: 'Fee Management',
      children: (
        <FeeManagementForm onSave={() => {
          message.success('Fee management updated successfully!');
        }} />
      ),
    }
  ];

  return (
    <div className="dashboard-container">
      <Card bordered={false}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={items}
          size="large"
        />
      </Card>

      {/* Manual Payout Detail Modal */}
      <ManualPayoutDetailModal
        visible={payoutModalVisible}
        transfer={transferDetails}
        loading={transferDetailsLoading}
        onClose={handlePayoutModalClose}
        onSuccess={handlePayoutSuccess}
      />
    </div>
  );
};

export default Dashboard;
